import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Store, Mail, Calendar, RefreshCw, X, LayoutList, LayoutGrid, ChevronRight,
  ArrowUpDown, AlertCircle, TrendingUp, BadgeCheck, Hourglass, UserPlus, Briefcase,
} from "lucide-react";
import Sidebar1 from "components/Sidebar1";
import { fetchWithToken } from "api/ApiHandler";
import {
  FONT_HEADING, PAGE_SIZES, formatDate, fromNow, getInitials, handleAuthError, joinedThisMonth,
  monthlySignups, SignupsChart, Kpi, SideCard, MiniStat, Pagination, Td, EmptyState, RowSkeleton, CardSkeleton,
} from "./shared";

const SORTS = {
  newest: { label: "Newest first", fn: (a, b) => new Date(b.createdAt) - new Date(a.createdAt) },
  oldest: { label: "Oldest first", fn: (a, b) => new Date(a.createdAt) - new Date(b.createdAt) },
  name: { label: "Business A–Z", fn: (a, b) => (a.businessName || "").localeCompare(b.businessName || "") },
};

const STATUS_STYLES = {
  active: { label: "Active", badge: "bg-[#DCFCE7] text-[#16A34A]", dot: "#16A34A" },
  pending: { label: "Pending", badge: "bg-[#FEF3C7] text-[#B45309]", dot: "#F59E0B" },
  rejected: { label: "Rejected", badge: "bg-[#FDECEA] text-[#EA352B]", dot: "#EA352B" },
  suspended: { label: "Suspended", badge: "bg-[#F3F4F6] text-[#374151]", dot: "#6B7280" },
};
export const statusMeta = (status) => {
  const key = (status || "").toLowerCase();
  return STATUS_STYLES[key] || { label: status || "Unknown", badge: "bg-[#F3F4F6] text-[#6B7280]", dot: "#9CA3AF" };
};

const getPhone = (p) => p?.phNumber || p?.phone || p?.businessPhone || "";
const detailsPath = (p) => `/user-management/service-provider/${p._id}`;

export default function ServiceProvider() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("newest");
  const [view, setView] = useState("list");
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [currentPage, setCurrentPage] = useState(1);

  const [providers, setProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const loadProviders = async () => {
    try {
      setIsLoading(true);
      setLoadError("");
      const data = await fetchWithToken("/admin/get-all-service-providers");
      setProviders(Array.isArray(data?.providers) ? data.providers : []);
    } catch (error) {
      console.error("Fetch service providers error:", error.response?.data || error);
      if (handleAuthError(error)) return;
      setLoadError("Unable to load service providers.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortKey, pageSize]);

  // Counts per status — tabs include any status the API returns, not just active/pending
  const statusCounts = useMemo(() => {
    const counts = { all: providers.length, active: 0, pending: 0 };
    providers.forEach((p) => {
      const k = (p.status || "unknown").toLowerCase();
      counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
  }, [providers]);

  const stats = useMemo(() => {
    const active = statusCounts.active || 0;
    return {
      total: providers.length,
      active,
      pending: statusCounts.pending || 0,
      activeRate: providers.length ? Math.round((active / providers.length) * 1000) / 10 : 0,
      ...joinedThisMonth(providers),
    };
  }, [providers, statusCounts]);

  const signupsByMonth = useMemo(() => monthlySignups(providers), [providers]);

  const filteredProviders = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return providers
      .filter((p) => {
        const matchesSearch =
          !term ||
          [p.businessName, p.fullName, p.businessEmail, getPhone(p)].some((v) => String(v || "").toLowerCase().includes(term));
        const matchesStatus = statusFilter === "all" || (p.status || "unknown").toLowerCase() === statusFilter;
        return matchesSearch && matchesStatus;
      })
      .sort(SORTS[sortKey].fn);
  }, [providers, searchTerm, statusFilter, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filteredProviders.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const currentItems = filteredProviders.slice(pageStart, pageStart + pageSize);

  const recentProviders = useMemo(() => [...providers].sort(SORTS.newest.fn).slice(0, 5), [providers]);

  const open = (p) => navigate(detailsPath(p));

  return (
    <div className="flex h-screen bg-[#F6F6F8] overflow-hidden">
      <Sidebar1 isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />

      <main className="flex-1 min-w-0 overflow-y-auto px-8 py-6 lg:px-6 md:px-4 sm:px-3">
        {/* Header */}
        <header className="flex items-center justify-between gap-4 mb-6 sm:flex-col sm:items-start">
          <div>
            <h1 className="m-0 text-3xl sm:text-2xl font-medium text-[#111]" style={FONT_HEADING}>Service Providers</h1>
            <p className="m-0 mt-1 text-sm text-[#6B7280]">Businesses that run shops on Fuelingo — open one to see its shops, services and reviews.</p>
          </div>
          <button
            type="button"
            onClick={loadProviders}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-xl bg-[#fff] border border-[#ECECEF] px-4 py-2.5 text-sm font-medium text-[#374151] hover:border-[#EA352B] hover:text-[#EA352B] transition disabled:opacity-60"
          >
            <RefreshCw size={15} className={isLoading ? "animate-spin" : ""} /> Refresh
          </button>
        </header>

        {/* KPIs */}
        <section className="grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-5 md:gap-4 mb-6">
          <Kpi Icon={Briefcase} label="Total providers" value={stats.total} loading={isLoading} />
          <Kpi Icon={BadgeCheck} label="Active" value={stats.active} sub={`${stats.activeRate}% of providers`} loading={isLoading} />
          <Kpi Icon={Hourglass} label="Pending approval" value={stats.pending} sub={stats.pending ? "Waiting for review" : "All caught up"} loading={isLoading} />
          <Kpi Icon={UserPlus} label="Joined this month" value={stats.thisMonth} change={stats.monthChange} loading={isLoading} />
        </section>

        <div className="grid grid-cols-[minmax(0,1fr)_340px] lg:grid-cols-[minmax(0,1fr)_300px] md:grid-cols-1 gap-6 md:gap-4">
          <section className="min-w-0 bg-[#fff] rounded-3xl border border-[#EEEEF1] p-6 md:p-5 sm:p-4 self-start" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
            {/* Toolbar */}
            <div className="flex items-center gap-3 flex-wrap mb-4">
              <div className="relative flex-1 min-w-[220px] sm:min-w-0 sm:w-full">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
                {/* No `type` attr on purpose: styles/tailwind.css resets padding/border on
                    [type="search"] with higher priority than these utility classes. */}
                <input
                  role="searchbox"
                  enterKeyHint="search"
                  onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search business, owner, email or phone"
                  className="w-full pl-10 pr-9 py-2.5 bg-[#F7F7F9] border border-[#EEEEF1] rounded-xl text-sm text-[#111] placeholder-[#9CA3AF] focus:outline-none focus:border-[#EA352B] focus:bg-[#fff] focus:ring-0 transition"
                />
                {searchTerm && (
                  <button type="button" aria-label="Clear search" onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111]">
                    <X size={15} />
                  </button>
                )}
              </div>

              <label className="relative inline-flex items-center sm:flex-1">
                <ArrowUpDown size={14} className="absolute left-3 text-[#9CA3AF] pointer-events-none" />
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  aria-label="Sort providers"
                  className="w-full appearance-none bg-[#F7F7F9] border border-[#EEEEF1] rounded-xl pl-8 pr-8 py-2.5 text-sm text-[#374151] focus:outline-none focus:border-[#EA352B] focus:ring-0 cursor-pointer"
                >
                  {Object.entries(SORTS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
                </select>
              </label>

              <div className="inline-flex rounded-xl bg-[#F3F3F6] p-1" role="group" aria-label="View">
                {[["list", LayoutList], ["grid", LayoutGrid]].map(([v, Icon]) => (
                  <button
                    key={v}
                    type="button"
                    aria-label={`${v} view`}
                    aria-pressed={view === v}
                    onClick={() => setView(v)}
                    className={`w-9 h-8 rounded-lg flex items-center justify-center transition ${view === v ? "bg-[#fff] text-[#EA352B] shadow-sm" : "text-[#9CA3AF] hover:text-[#111]"}`}
                  >
                    <Icon size={16} />
                  </button>
                ))}
              </div>
            </div>

            {/* Status tabs */}
            <div className="flex gap-2 flex-wrap mb-5">
              {Object.keys(statusCounts).map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => setStatusFilter(st)}
                  className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                    statusFilter === st ? "bg-[#EA352B] text-[#fff] shadow-sm" : "bg-[#F3F3F6] text-[#6B7280] hover:text-[#111]"
                  }`}
                >
                  {st !== "all" && <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusFilter === st ? "#fff" : statusMeta(st).dot }} />}
                  {st === "all" ? "All" : statusMeta(st).label}
                  <span className={`rounded-full px-1.5 py-px text-[10px] ${statusFilter === st ? "bg-[#ffffff33]" : "bg-[#fff]"}`}>
                    {isLoading ? "…" : statusCounts[st]}
                  </span>
                </button>
              ))}
            </div>

            {loadError ? (
              <EmptyState
                Icon={AlertCircle}
                title={loadError}
                text="Check your connection and try again."
                action={<button type="button" onClick={loadProviders} className="mt-3 rounded-xl bg-[#EA352B] text-[#fff] px-4 py-2 text-sm font-medium">Try again</button>}
              />
            ) : !isLoading && currentItems.length === 0 ? (
              <EmptyState
                Icon={Search}
                title="No service providers found"
                text={searchTerm || statusFilter !== "all" ? "Try a different search or filter." : "Providers will appear here once they register."}
              />
            ) : view === "list" ? (
              <>
                <div className="sm:hidden overflow-x-auto -mx-6 md:-mx-5 px-6 md:px-5">
                  <table className="w-full min-w-[680px] text-left border-separate border-spacing-0">
                    <thead>
                      <tr>
                        {["Business", "Contact", "Joined", "Status", ""].map((h, i) => (
                          <th key={i} className="py-3 px-3 first:pl-0 text-[11px] font-semibold uppercase tracking-wider text-[#9CA3AF] border-b border-[#EEEEF1] whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading
                        ? Array.from({ length: 6 }).map((_, i) => <RowSkeleton key={i} widths={[28, 20, 16, 4]} />)
                        : currentItems.map((p) => {
                          const st = statusMeta(p.status);
                          return (
                            <tr
                              key={p._id}
                              tabIndex={0}
                              role="link"
                              aria-label={`Open ${p.businessName || "provider"}`}
                              onClick={() => open(p)}
                              onKeyDown={(e) => e.key === "Enter" && open(p)}
                              className="group cursor-pointer hover:bg-[#FFF7F6] focus:outline-none focus-visible:bg-[#FFF7F6] transition-colors"
                            >
                              <Td>
                                <div className="flex items-center gap-3 min-w-0">
                                  <BusinessAvatar name={p.businessName} />
                                  <div className="min-w-0">
                                    <p className="m-0 text-sm font-medium text-[#111] truncate group-hover:text-[#EA352B] transition-colors">{p.businessName || "Unnamed business"}</p>
                                    <p className="m-0 text-[12px] text-[#9CA3AF] truncate">{p.fullName || "—"}</p>
                                  </div>
                                </div>
                              </Td>
                              <Td>
                                <p className="m-0 text-sm text-[#374151] truncate max-w-[220px]">{p.businessEmail || "—"}</p>
                                {getPhone(p) && <p className="m-0 text-[11px] text-[#9CA3AF]">{getPhone(p)}</p>}
                              </Td>
                              <Td>
                                <p className="m-0 text-sm text-[#374151]">{formatDate(p.createdAt)}</p>
                                <p className="m-0 text-[11px] text-[#9CA3AF]">{fromNow(p.createdAt)}</p>
                              </Td>
                              <Td><StatusBadge meta={st} /></Td>
                              <Td className="w-8 text-right">
                                <ChevronRight size={16} className="text-[#C0C4CC] group-hover:text-[#EA352B] group-hover:translate-x-0.5 transition" />
                              </Td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                <ul className="hidden sm:block m-0 pl-0 list-none divide-y divide-[#F1F1F4]">
                  {isLoading
                    ? Array.from({ length: 6 }).map((_, i) => <li key={i} className="py-3"><CardSkeleton compact /></li>)
                    : currentItems.map((p) => (
                      <li key={p._id}>
                        <button type="button" onClick={() => open(p)} className="w-full text-left flex items-center gap-3 py-3">
                          <BusinessAvatar name={p.businessName} />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-[#111] truncate">{p.businessName || "Unnamed business"}</span>
                            <span className="block text-[11px] text-[#9CA3AF] truncate">{p.fullName} · {formatDate(p.createdAt)}</span>
                          </span>
                          <StatusBadge meta={statusMeta(p.status)} />
                        </button>
                      </li>
                    ))}
                </ul>
              </>
            ) : (
              <div className="grid grid-cols-3 lg:grid-cols-2 sm:grid-cols-1 gap-4">
                {isLoading
                  ? Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)
                  : currentItems.map((p) => (
                    <button
                      key={p._id}
                      type="button"
                      onClick={() => open(p)}
                      className="group text-left rounded-2xl border border-[#EEEEF1] bg-[#FAFAFB] p-4 hover:bg-[#fff] hover:border-[#F6C9C5] hover:shadow-[0_8px_24px_rgba(234,53,43,0.08)] hover:-translate-y-0.5 transition-all"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <BusinessAvatar name={p.businessName} size={44} />
                        <StatusBadge meta={statusMeta(p.status)} />
                      </div>
                      <p className="m-0 mt-3 text-sm font-semibold text-[#111] truncate group-hover:text-[#EA352B] transition-colors">{p.businessName || "Unnamed business"}</p>
                      <p className="m-0 text-[12px] text-[#6B7280] truncate">{p.fullName || "—"}</p>
                      <div className="mt-3 pt-3 border-t border-[#EEEEF1] space-y-1">
                        <p className="m-0 text-[11px] text-[#6B7280] inline-flex items-center gap-1.5 truncate max-w-full"><Mail size={12} className="shrink-0" /> <span className="truncate">{p.businessEmail || "—"}</span></p>
                        <p className="m-0 text-[11px] text-[#6B7280] flex items-center gap-1.5"><Calendar size={12} /> Joined {formatDate(p.createdAt)}</p>
                      </div>
                    </button>
                  ))}
              </div>
            )}

            {!isLoading && !loadError && filteredProviders.length > 0 && (
              <div className="flex items-center justify-between gap-3 flex-wrap mt-6 pt-4 border-t border-[#F1F1F4]">
                <div className="flex items-center gap-3 text-xs text-[#6B7280]">
                  <span>
                    Showing <b className="text-[#111]">{pageStart + 1}–{Math.min(pageStart + pageSize, filteredProviders.length)}</b> of{" "}
                    <b className="text-[#111]">{filteredProviders.length}</b>
                  </span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    aria-label="Rows per page"
                    className="bg-[#F7F7F9] border border-[#EEEEF1] rounded-lg py-1 pl-2 pr-7 text-xs focus:outline-none focus:ring-0 focus:border-[#EA352B]"
                  >
                    {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / page</option>)}
                  </select>
                </div>
                {totalPages > 1 && <Pagination page={currentPage} total={totalPages} onChange={setCurrentPage} />}
              </div>
            )}
          </section>

          <aside className="min-w-0 space-y-6 md:space-y-0 md:grid md:grid-cols-2 md:gap-4 sm:grid-cols-1">
            <SideCard title="Approval status" Icon={BadgeCheck}>
              {isLoading ? (
                <div className="h-24 rounded-xl bg-[#F3F3F6] animate-pulse" />
              ) : (
                <>
                  <div className="flex items-end justify-between">
                    <span className="text-3xl font-semibold text-[#111]">{stats.activeRate}%</span>
                    <span className="text-xs text-[#6B7280]">active</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-[#F1F1F4] overflow-hidden mt-3 flex">
                    {Object.entries(statusCounts)
                      .filter(([k]) => k !== "all")
                      .map(([k, n]) => (
                        <div key={k} className="h-full transition-all duration-700" style={{ width: `${stats.total ? (n / stats.total) * 100 : 0}%`, background: statusMeta(k).dot }} />
                      ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    {Object.entries(statusCounts)
                      .filter(([k]) => k !== "all")
                      .map(([k, n]) => <MiniStat key={k} color={statusMeta(k).dot} label={statusMeta(k).label} value={n} />)}
                  </div>
                </>
              )}
            </SideCard>

            <SideCard title="Signups · last 6 months" Icon={TrendingUp}>
              <SignupsChart data={signupsByMonth} loading={isLoading} />
            </SideCard>

            <SideCard title="Recently joined" Icon={Store}>
              <div className="space-y-2">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => <CardSkeleton key={i} compact />)
                ) : recentProviders.length === 0 ? (
                  <p className="m-0 text-sm text-[#9CA3AF] text-center py-6">No service providers yet.</p>
                ) : (
                  recentProviders.map((p) => (
                    <button
                      key={p._id}
                      type="button"
                      onClick={() => open(p)}
                      className="w-full text-left flex items-center gap-3 rounded-2xl p-2.5 hover:bg-[#F7F7F9] transition"
                    >
                      <BusinessAvatar name={p.businessName} />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-[#111] truncate">{p.businessName || "Unnamed business"}</span>
                        <span className="block text-[11px] text-[#9CA3AF]">{fromNow(p.createdAt)}</span>
                      </span>
                      <StatusBadge meta={statusMeta(p.status)} />
                    </button>
                  ))
                )}
              </div>
            </SideCard>
          </aside>
        </div>
      </main>
    </div>
  );
}

const AVATAR_BG = ["#EA352B", "#FF8A3D", "#1B1B22", "#F5B400", "#6B7280"];

export function BusinessAvatar({ name = "", size = 40, src }) {
  const [broken, setBroken] = useState(false);
  const bg = AVATAR_BG[name.length % AVATAR_BG.length];
  if (src && !broken) {
    return (
      <img src={src} alt={name} onError={() => setBroken(true)} className="rounded-xl object-cover shrink-0 bg-[#F3F3F6]" style={{ width: size, height: size }} />
    );
  }
  return (
    <span
      className="rounded-xl flex items-center justify-center font-semibold text-[#fff] shrink-0"
      style={{ background: bg, width: size, height: size, fontSize: Math.round(size / 2.8) }}
    >
      {getInitials(name) === "?" ? "SP" : getInitials(name)}
    </span>
  );
}

export function StatusBadge({ meta }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap ${meta.badge}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.dot }} />
      {meta.label}
    </span>
  );
}
